import {
  driver,
  type StandardEngine,
  type StyleObject,
} from '@pinecast/styletron-standard';
import * as React from 'react';

export type {StyleObject};

// If no engine is provided, styling is a no-op rather than a crash. Handy in tests.
const noopEngine: StandardEngine = {
  renderStyle: () => '',
  renderKeyframes: () => '',
  renderFontFace: () => '',
};

const StyletronContext = React.createContext<StandardEngine>(noopEngine);

export const Provider = StyletronContext.Provider;

function checkNoopEngine(engine: StandardEngine) {
  if (engine !== noopEngine || process.env.NODE_ENV === 'test') {
    return;
  }
  console.warn(
    process.env.NODE_ENV !== 'production'
      ? 'A styled component rendered, but no Styletron engine was provided in React context. ' +
          'Styling is a no-op. Did you forget to mount the Provider from @pinecast/styles/provider?'
      : 'Styletron Provider is not set up. Defaulting to no-op.',
  );
}

/**
 * Returns a single-element tuple containing the `css` function, which renders a
 * style object through the engine in context and returns the resulting atomic
 * class names.
 *
 * The tuple is upstream's shape, kept so `@pinecast/styles`' `useCSS()` reads as
 * `const [css] = useStyletron()`.
 */
export function useStyletron(): [(style: StyleObject) => string] {
  const styletronEngine = React.useContext(StyletronContext);
  checkNoopEngine(styletronEngine);

  return [
    function css(style: StyleObject) {
      return driver(style, styletronEngine);
    },
  ];
}
